import * as React from "react";
import { Link } from "@tanstack/react-router";

import { NAP, SHOW_PHONE } from "@/lib/business";
import { aiServiceLinks, companyLinks, resourceLinks, legalLinks, DISCLAIMER } from "@/lib/nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function Disclaimer() {
  return (
    <p className="mx-auto max-w-[68ch] pt-6 text-body-sm leading-relaxed text-ondark-muted">
      {DISCLAIMER}
    </p>
  );
}

function NapBlock() {
  return (
    <address className="not-italic space-y-1 text-body-sm text-ondark-body">
      <p className="text-paper-0">{NAP.legalName}</p>
      <p>{NAP.street}</p>
      <p>
        {NAP.city}, {NAP.region} {NAP.postalCode}
      </p>
      {SHOW_PHONE ? (
        <p>
          <a
            href={NAP.phoneHref}
            className="inline-flex min-h-[44px] min-w-[44px] items-center transition-brand hover:text-paper-0"
          >
            {NAP.phone}
          </a>
        </p>
      ) : null}
      <p>
        <a
          href={`mailto:${NAP.email}`}
          className="inline-flex min-h-[44px] min-w-[44px] items-center transition-brand hover:text-paper-0"
        >
          {NAP.email}
        </a>
      </p>
      <p>{NAP.hours}</p>
    </address>
  );
}

function NewsletterBlock() {
  // TODO: connect to the ESP. No provider is wired up yet.
  const [submitted, setSubmitted] = React.useState(false);

  function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitted(true);
  }

  return (
    <form
      onSubmit={onSubmit}
      className="flex flex-col gap-4 border-t border-paper-0/12 py-10 md:flex-row md:items-center md:justify-between"
    >
      <p className="max-w-[52ch] text-body text-ondark-body">
        One email a month: where AI is actually working in small businesses, and what it cost them.
      </p>

      <div className="flex w-full max-w-md items-end gap-3">
        <div className="flex-1">
          <Label htmlFor="footer-email" className="sr-only">
            Email address
          </Label>
          <Input
            id="footer-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="you@company.com"
          />
        </div>
        <Button type="submit" size="sm">
          Subscribe
        </Button>
      </div>
      <p aria-live="polite" className="sr-only">
        {submitted ? "Thanks — you're on the list." : ""}
      </p>
    </form>
  );
}

function LegalBar() {
  return (
    <div className="flex flex-col gap-3 border-t border-paper-0/12 py-6 text-body-sm text-ondark-muted md:flex-row md:items-center md:justify-between">
      <p>© 2026 LAA Concierge Consulting, LLC</p>
      <ul className="flex flex-wrap gap-x-6 gap-y-2">
        {legalLinks.map((link) => (
          <li key={link.to}>
            <Link
              to={link.to}
              className="inline-flex min-h-[44px] min-w-[44px] items-center transition-brand hover:text-paper-0"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FooterShell({ children }: { children: React.ReactNode }) {
  return (
    <footer className="dark w-full bg-ink-900 text-paper-0">
      <div
        className="mx-auto w-full py-16 pb-[calc(4rem+3.5rem+env(safe-area-inset-bottom))] md:pb-16"
        style={{ maxWidth: "1200px", paddingInline: "clamp(20px, 5vw, 48px)" }}
      >
        {children}
      </div>
    </footer>
  );
}

function LinkColumn({
  title,
  links,
}: {
  title: string;
  links: readonly { label: string; to: string }[];
}) {
  return (
    <div>
      <h2 className="font-mono text-eyebrow uppercase tracking-[0.16em] text-paper-0">{title}</h2>
      <ul className="mt-1 space-y-2">
        {links.map((link) => (
          <li key={link.to}>
            <Link
              to={link.to as never}
              className="inline-flex min-h-[44px] items-center text-body-sm text-ondark-muted transition-brand hover:text-paper-0"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ServicesColumn() {
  // AI services only. The funding pages stay live and indexable but are no
  // longer in site-wide chrome — the homepage funding band is their entry point.
  return (
    <div>
      <h2 className="font-mono text-eyebrow uppercase tracking-[0.16em] text-paper-0">Services</h2>
      <ul className="mt-1 space-y-2">
        {aiServiceLinks.map((link) => (
          <li key={link.to}>
            <Link
              to={link.to}
              className="inline-flex min-h-[44px] items-center text-body-sm text-ondark-muted transition-brand hover:text-paper-0"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  return (
    <FooterShell>
      <div className="grid gap-10 pb-12 md:grid-cols-2 lg:grid-cols-4">
        <ServicesColumn />
        <LinkColumn title="Company" links={companyLinks} />
        <LinkColumn title="Resources" links={resourceLinks} />
        <div>
          <h2 className="font-mono text-eyebrow uppercase tracking-[0.16em] text-paper-0">
            Contact
          </h2>
          <div className="mt-4">
            <NapBlock />
            <Link
              to="/book"
              className="mt-2 inline-flex min-h-[44px] items-center text-body-sm font-medium text-brand-300 transition-brand hover:text-brand-400"
            >
              Book a call
            </Link>
          </div>
        </div>
      </div>
      <NewsletterBlock />
      <LegalBar />
      <Disclaimer />
    </FooterShell>
  );
}

export function FooterMinimal() {
  return (
    <FooterShell>
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <NapBlock />
        <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 text-body-sm text-ondark-muted">
          {legalLinks.map((link) => (
            <li key={link.to}>
              <Link
                to={link.to}
                className="inline-flex min-h-[44px] min-w-[44px] items-center transition-brand hover:text-paper-0"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <Disclaimer />
    </FooterShell>
  );
}

export default Footer;
